/*
"Générer un itinéraire" sheet — opens from the species detail screen.

The user picks a target distance (1 / 2 / 3 / 5 km presets) and the
sheet calls back with the chosen value. The species detail screen
then runs the planner against the user's current GPS, stashes the
plan in `routeState`, and navigates to the map.

Why a separate sheet instead of inline buttons on the species card:
the route planner needs the user to confirm intent (a tap is
cheaper than a "Generate" affordance hidden in a long card), and
the distance picker is the natural place to hint at the GPS
prerequisite if no fix is available.
*/

const kSheetId: string = 'route-plan-sheet'

export interface RoutePlanSheetOptions
{
	speciesLabel: string  // shown in the eyebrow so the user remembers what they're routing for
	hasFix: boolean       // false when no usable GPS fix is in cache — disables the action buttons + surfaces a hint
	onPick: (inDistanceMeters: number) => void
}

const kPresetMeters: readonly { label: string; meters: number }[] = [
	{ label: '1 km', meters: 1000 },
	{ label: '2 km', meters: 2000 },
	{ label: '3 km', meters: 3000 },
	{ label: '5 km', meters: 5000 },
]

/*
Open the sheet. Returns a close function for callers that want to
dismiss imperatively. The sheet self-destructs on action / dismiss.
*/
export function openRoutePlanSheet(inOpts: RoutePlanSheetOptions): () => void
{
	const vExisting = document.getElementById(kSheetId)
	if (vExisting) vExisting.remove()

	const vRoot = document.createElement('div')
	vRoot.id = kSheetId
	vRoot.className = 'route-plan-sheet'
	vRoot.setAttribute('role', 'dialog')
	vRoot.setAttribute('aria-modal', 'true')
	vRoot.innerHTML = `
		<div class="route-plan-sheet-backdrop"></div>
		<div class="route-plan-sheet-panel">
			<header class="route-plan-sheet-header">
				<div class="route-plan-sheet-title-block">
					<div class="route-plan-sheet-eyebrow">Itinéraire de découverte</div>
					<div class="route-plan-sheet-title">${escapeHtml(inOpts.speciesLabel)}</div>
				</div>
				<button class="route-plan-sheet-close" type="button" aria-label="Fermer">×</button>
			</header>
			<div class="route-plan-sheet-body">
				${inOpts.hasFix
					? `<p class="route-plan-sheet-hint">Choisis la longueur souhaitée. L'itinéraire part de ta position actuelle et enchaîne les arbres les plus proches de cette espèce.</p>`
					: `<p class="route-plan-sheet-hint route-plan-sheet-hint-warn">Active le GPS d'abord — l'itinéraire part de ta position actuelle.</p>`}
				<div class="route-plan-sheet-grid">
					${kPresetMeters.map((inP) => `
						<button class="route-plan-sheet-preset" type="button" data-meters="${inP.meters}" ${inOpts.hasFix ? '' : 'disabled'}>${inP.label}</button>
					`).join('')}
				</div>
			</div>
		</div>
	`
	document.body.appendChild(vRoot)
	requestAnimationFrame(() => vRoot.setAttribute('data-open', 'true'))

	let vClosed: boolean = false
	const vClose = (): void => {
		if (vClosed) return
		vClosed = true
		vRoot.setAttribute('data-open', 'false')
		document.removeEventListener('keydown', vOnKey)
		window.setTimeout(() => vRoot.remove(), 220)
	}

	const vOnKey = (inEv: KeyboardEvent): void => { if (inEv.key === 'Escape') vClose() }
	document.addEventListener('keydown', vOnKey)

	vRoot.querySelector<HTMLButtonElement>('.route-plan-sheet-close')?.addEventListener('click', vClose)
	vRoot.querySelector<HTMLElement>('.route-plan-sheet-backdrop')?.addEventListener('click', vClose)

	vRoot.querySelectorAll<HTMLButtonElement>('.route-plan-sheet-preset').forEach((inBtn) => {
		inBtn.addEventListener('click', () => {
			const vMeters = Number(inBtn.dataset.meters)
			if (!Number.isFinite(vMeters)) return
			vClose()
			inOpts.onPick(vMeters)
		})
	})

	return vClose
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}
